<template>
  <Dialog
    :header="$t('payment.title')"
    :visible="visible"
    @update:visible="close"
    :modal="true"
    :draggable="false"
    style="width: 420px; border-radius: 6px"
  >
    <!-- Step 1: pick an online payment method -->
    <div v-if="step === 'method'" class="pay-body">
      <div v-if="amount" class="amount-row">
        {{ $t("payment.amount") }}: <strong>{{ formatAmount(amount) }}</strong>
      </div>
      <p class="hint">{{ $t("payment.choose_method") }}</p>

      <div v-if="loadingMethods" class="muted">…</div>
      <div v-else-if="!methods.length" class="muted">{{ $t("payment.no_online_methods") }}</div>
      <div v-else class="methods">
        <label
          v-for="m in methods"
          :key="m.id"
          class="method"
          :class="{ selected: selectedMethodId === m.id }"
        >
          <input type="radio" :value="m.id" v-model="selectedMethodId" />
          <img
            v-if="iconUrl(m)"
            :src="iconUrl(m)"
            class="method-icon"
            alt=""
            @error="onIconError"
          />
          <span>{{ primaryName(m) }}</span>
        </label>
      </div>

      <div class="actions">
        <button class="btn-secondary" :disabled="busy" @click="close">{{ $t("payment.cancel") }}</button>
        <button class="btn-primary" :disabled="!selectedMethodId || busy" @click="startPayment">
          {{ $t("payment.pay") }}
        </button>
      </div>
    </div>

    <!-- Step 2: enter the OTP (OTP-based gateways, e.g. Syriatel Cash) -->
    <div v-else-if="step === 'otp'" class="pay-body">
      <p class="hint">{{ $t("payment.otp_hint") }}</p>
      <label class="otp-label">{{ $t("payment.otp_label") }}</label>
      <InputText
        v-model="otp"
        class="otp-input"
        inputmode="numeric"
        autocomplete="one-time-code"
        @keyup.enter="confirmOtp"
      />
      <div class="actions">
        <button class="btn-secondary" :disabled="busy" @click="resendOtp">{{ $t("payment.resend") }}</button>
        <button class="btn-primary" :disabled="!otp || busy" @click="confirmOtp">{{ $t("payment.confirm") }}</button>
      </div>
    </div>
  </Dialog>
</template>

<script>
import axios from "axios";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";

export default {
  name: "PaymentDialog",
  components: { Dialog, InputText },
  props: {
    visible: { type: Boolean, default: false },
    bookingId: { required: true },
    amount: { type: [Number, String], default: null },
  },
  emits: ["update:visible", "paid"],
  data() {
    return {
      step: "method",
      methods: [],
      loadingMethods: false,
      selectedMethodId: null,
      busy: false,
      logId: null,
      otp: "",
    };
  },
  computed: {
    api() {
      return this.$store.state.rootAPI;
    },
  },
  watch: {
    visible(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      this.step = "method";
      this.selectedMethodId = null;
      this.logId = null;
      this.otp = "";
      this.busy = false;
      this.loadMethods();
    },
    primaryName(m) {
      return this.$store.getters.selectedLanguage === "ar"
        ? m.name_ar || m.name_en
        : m.name_en || m.name_ar;
    },
    iconUrl(m) {
      if (!m || !m.icon) return null;
      // Strip the "/api/passenger/" (or "/api/") API suffix to get the host root
      // that serves the stored gateway icons.
      const host = String(this.api).replace(/\/api(\/passenger)?\/?$/, "/");
      const path = String(m.icon).replace(/^\//, "");
      return host + path;
    },
    onIconError(e) {
      // Degrade gracefully to the name-only display if the icon fails to load.
      if (e && e.target) e.target.style.display = "none";
    },
    formatAmount(v) {
      // Redenominated display (value / 100, 2 decimals) via the global money mixin.
      return this.formatMoney(v) + " SYP";
    },
    close() {
      this.$emit("update:visible", false);
    },
    loadMethods() {
      this.loadingMethods = true;
      axios
        .get(this.api + "payment-methods")
        .then((res) => {
          const all = (res.data && res.data.data) || [];
          this.methods = all.filter((m) => m.online_payment);
        })
        .catch(() => {
          this.methods = [];
        })
        .finally(() => {
          this.loadingMethods = false;
        });
    },
    startPayment() {
      if (!this.selectedMethodId) return;
      this.busy = true;
      axios
        .put(`${this.api}booking/${this.bookingId}/pay`, { payment_method_id: this.selectedMethodId })
        .then((res) => {
          const log = (res.data && res.data.data) || {};
          const pr = log.payment_response || {};
          const redirectUrl = pr.url || pr.redirect_url || pr.checkout_url || pr.payment_url;

          if (pr.requiresOtp || pr.status === "OTP_SENT") {
            // OTP-based gateway: move to the verification step.
            this.logId = log.id;
            this.step = "otp";
            this.$notify({
              title: this.$t("general.success"),
              text: this.$t("payment.otp_sent"),
              type: "success",
            });
          } else if (redirectUrl) {
            // Redirect/checkout gateway (eCash, Paymera): the booking is only
            // settled after the gateway callback, NOT now. Open the hosted page;
            // do not mark it paid here.
            window.open(redirectUrl, "_blank");
            this.$notify({
              title: this.$t("general.success"),
              text: this.$t("payment.redirect_opened"),
              type: "success",
            });
            this.close();
          } else {
            // Unexpected response shape: do NOT mark the booking as paid.
            this.$notify({
              title: this.$t("general.error"),
              text: this.$t("general.error"),
              type: "error",
            });
          }
        })
        .catch((err) => this.notifyError(err))
        .finally(() => {
          this.busy = false;
        });
    },
    confirmOtp() {
      if (!this.otp) return;
      this.busy = true;
      axios
        .post(`${this.api}payment/otp/${this.logId}/confirm`, { otp: this.otp })
        .then(() => {
          this.$emit("paid");
          this.$notify({
            title: this.$t("general.success"),
            text: this.$t("payment.paid_success"),
            type: "success",
          });
          this.close();
        })
        .catch((err) => this.notifyError(err))
        .finally(() => {
          this.busy = false;
        });
    },
    resendOtp() {
      if (!this.logId) return;
      this.busy = true;
      axios
        .post(`${this.api}payment/otp/${this.logId}/resend`)
        .then(() =>
          this.$notify({
            title: this.$t("general.success"),
            text: this.$t("payment.resent"),
            type: "success",
          })
        )
        .catch((err) => this.notifyError(err))
        .finally(() => {
          this.busy = false;
        });
    },
    notifyError(err) {
      const data = err && err.response && err.response.data;
      const msg =
        (data && (data.message || (data.data && data.data.message))) || this.$t("general.error");
      this.$notify({ title: this.$t("general.error"), text: msg, type: "error" });
    },
  },
};
</script>

<style lang="scss" scoped>
.pay-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 320px;
}

.amount-row {
  font-size: 15px;
  color: #222629;
}

.hint {
  font-size: 14px;
  color: #788e98;
  margin: 0;
}

.muted {
  font-size: 14px;
  color: #a3a4ae;
  padding: 8px 0;
}

.methods {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.method-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.method {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #c1c5d8;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;

  &.selected {
    border-color: #e10015;
    box-shadow: inset 0 0 0 1px #e10015;
  }

  input {
    accent-color: #e10015;
    cursor: pointer;
  }
}

.otp-label {
  font-size: 14px;
  color: #788e98;
}

.otp-input {
  width: 100%;
  letter-spacing: 4px;
  text-align: center;
  font-size: 18px;
}

.actions {
  display: flex;
  gap: 10px;
  margin-top: 6px;
}

.btn-primary,
.btn-secondary {
  flex: 1;
  border-radius: 6px;
  padding: 10px 0;
  font-size: 15px;
  cursor: pointer;
  border: 1px solid;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-primary {
  background: #e10015;
  color: #fff;
  border-color: #e10015;
}

.btn-secondary {
  background: #fff;
  color: #222629;
  border-color: #c1c5d8;
}
</style>
